import { Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { BrandButton } from "./brand/BrandButton";
import { useTranslation } from "react-i18next";
import { supportedLanguages, languageNames, setStoredLanguage } from "../i18n";
import type { SupportedLanguage } from "../i18n";

/**
 * LanguageSwitcher Component
 * Dropdown menu for switching between supported languages
 *
 * Features:
 * - Displays current language
 * - Lists all supported languages
 * - Persists language preference to localStorage
 * - Updates UI immediately on language change
 */
export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language as SupportedLanguage;

  /**
   * Handle language change
   * Updates i18n instance and stores preference
   */
  const handleLanguageChange = async (
    language: SupportedLanguage,
  ): Promise<void> => {
    try {
      console.log(
        "[LanguageSwitcher] Changing language from",
        currentLanguage,
        "to",
        language,
      );
      await i18n.changeLanguage(language);
      setStoredLanguage(language);
      console.log(
        "[LanguageSwitcher] Language changed successfully to",
        language,
      );
    } catch (error) {
      console.error("[LanguageSwitcher] Failed to change language:", error);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <BrandButton
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
          aria-label="Change language"
        >
          <Languages className="h-5 w-5" />
        </BrandButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-48 bg-background border-border shadow-lg"
      >
        <DropdownMenuLabel className="text-foreground">
          Language / 语言
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border" />
        {supportedLanguages.map((lang) => (
          <DropdownMenuItem
            key={lang}
            onClick={() => handleLanguageChange(lang)}
            className={`
              cursor-pointer
              ${
                currentLanguage === lang
                  ? "bg-primary/10 text-foreground border-l-2 border-border"
                  : "text-muted-foreground hover:text-foreground"
              }
              hover:bg-secondary focus:bg-secondary
            `}
          >
            <span className="flex items-center gap-2">
              {languageNames[lang]}
              {currentLanguage === lang && (
                <span className="ml-auto text-primary">✓</span>
              )}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
